import React, { useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Volume2, Music } from 'lucide-react';
import { useGameStateContext } from '../app/GameProviders';
import { Howl } from 'howler';
import { ASSETS } from '../assetManifest.js';
import { CONSTANTS } from '../data.js';

interface Props {
  show: boolean;
  onClose: () => void;
}

export function SettingsMenu({ show, onClose }: Props) {
  const { settings, setSettings } = useGameStateContext();

  const playTestSound = useCallback(() => {
    const sfxAssets = (ASSETS as any)?.sfx;
    if (!sfxAssets) return;

    const soundAsset = sfxAssets.settings_test;
    if (!soundAsset || !soundAsset.path) return;

    const volume = (settings.sfxVolume / 100) * CONSTANTS.SOUND_EFFECTS.SETTINGS_TEST_VOLUME;
    
    const sound = new Howl({
      src: [soundAsset.path],
      volume: volume,
      preload: true,
    });

    sound.play();
  }, [settings.sfxVolume]);

  const handleMusicVolumeChange = (value: number) => {
    setSettings(prev => ({ ...prev, musicVolume: value }));
  };

  const handleSfxVolumeChange = (value: number) => {
    setSettings(prev => ({ ...prev, sfxVolume: value }));
    // Play test sound when SFX volume changes
    playTestSound();
  };

  if (!show) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-gradient-to-br from-slate-900/95 via-amber-900/20 to-orange-900/30 z-[3000] flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-gradient-to-br from-slate-800/95 via-amber-900/10 to-orange-900/10 backdrop-blur-md rounded-xl shadow-2xl w-full max-w-md border border-amber-500/30"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-amber-500/20 bg-gradient-to-r from-amber-600/10 to-orange-600/10 rounded-t-xl">
            <h2 className="text-2xl font-bold text-amber-100">Kitchen Settings</h2>
            <button
              onClick={onClose}
              className="text-amber-300 hover:text-white p-1 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-8">
            {/* Music Volume */}
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Music className="w-5 h-5 text-amber-400" />
                <label className="text-lg font-medium text-amber-100">Music Volume</label>
              </div>
              <div className="space-y-2">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={settings.musicVolume}
                  onChange={(e) => handleMusicVolumeChange(parseInt(e.target.value))}
                  className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer slider"
                  style={{
                    background: `linear-gradient(to right, #f59e0b 0%, #f59e0b ${settings.musicVolume}%, #374151 ${settings.musicVolume}%, #374151 100%)`
                  }}
                />
                <div className="flex justify-between text-sm text-amber-200">
                  <span>0%</span>
                  <span className="text-amber-300 font-medium">{settings.musicVolume}%</span>
                  <span>100%</span>
                </div>
              </div>
            </div>

            {/* Sound Effects Volume */}
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Volume2 className="w-5 h-5 text-orange-400" />
                <label className="text-lg font-medium text-amber-100">Sound Effects Volume</label>
              </div>
              <div className="space-y-2">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={settings.sfxVolume}
                  onChange={(e) => handleSfxVolumeChange(parseInt(e.target.value))}
                  className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer slider"
                  style={{
                    background: `linear-gradient(to right, #ea580c 0%, #ea580c ${settings.sfxVolume}%, #374151 ${settings.sfxVolume}%, #374151 100%)`
                  }}
                />
                <div className="flex justify-between text-sm text-amber-200">
                  <span>0%</span>
                  <span className="text-orange-300 font-medium">{settings.sfxVolume}%</span>
                  <span>100%</span>
                </div>
              </div>
              <div className="text-xs text-slate-400 italic">
                Adjust the slider to test sound volume
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-amber-500/20 bg-gradient-to-r from-amber-600/10 to-orange-600/10 rounded-b-xl">
            <button
              onClick={onClose}
              className="w-full bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 text-white py-3 rounded-lg font-semibold transition-all duration-200"
            >
              Close
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
